"use client";

import { useEffect, useRef } from "react";

/**
 * Silently activates new service workers and reloads the page once to pick up updates.
 */
export function AutoUpdateListener() {
  const hasRefreshed = useRef(false);

  useEffect(() => {
    if (typeof window === "undefined" || !("serviceWorker" in navigator)) {
      return;
    }

    const activateWaitingServiceWorker = () => {
      navigator.serviceWorker.ready.then((registration) => {
        if (registration.waiting) {
          console.log("[PWA] Activating waiting service worker");
          registration.waiting.postMessage({ type: "SKIP_WAITING" });
        }
      });
    };

    const handleControllerChange = () => {
      if (hasRefreshed.current) {
        return;
      }
      hasRefreshed.current = true;
      console.log("[PWA] Controller changed - reloading to apply update");
      window.location.reload();
    };

    const handleMessage = (event: MessageEvent) => {
      if (
        event.data &&
        (event.data.type === "SW_UPDATED" || event.data.type === "UPDATE_AVAILABLE")
      ) {
        console.log("[PWA] Update detected, activating new service worker");
        activateWaitingServiceWorker();
      }
    };

    navigator.serviceWorker.addEventListener("message", handleMessage);
    navigator.serviceWorker.addEventListener("controllerchange", handleControllerChange);

    navigator.serviceWorker.ready.then((registration) => {
      if (registration.waiting) {
        activateWaitingServiceWorker();
      }

      registration.addEventListener("updatefound", () => {
        const newWorker = registration.installing;
        if (newWorker) {
          newWorker.addEventListener("statechange", () => {
            if (newWorker.state === "installed" && navigator.serviceWorker.controller) {
              activateWaitingServiceWorker();
            }
          });
        }
      });
    });

    return () => {
      navigator.serviceWorker.removeEventListener("message", handleMessage);
      navigator.serviceWorker.removeEventListener("controllerchange", handleControllerChange);
    };
  }, []);

  return null;
}
